import {
	type BulkDocumentRequest,
	type BulkDocumentOutcome,
	type TemplateSummary,
	ArtifactGenerationApiFactory,
	GenerateArtifactRequestDeliveryEnum,
	GenerateArtifactRequestFormatEnum,
	GenerateArtifactRequestStageEnum,
	GenerateArtifactsBulkRequestDeliveryEnum,
	TemplateDiscoveryApiFactory,
} from "@eu.mdotm.api/storyfolio-gateway";
import { ServiceType } from "@eu.mdotm.api/sphere";
import { getStoryfolioGatewayApi, storyfolioGatewayAvailable } from "$root/api/storyfolio-gateway";
import { repositoryHelperFactory } from "$root/api/repository";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { getCurrentUser } from "$root/functional-areas/user";
import { axiosExtract } from "$root/third-party-integrations/axios";

// Storyfolio V2: the Storyfolio gateway's template catalogue, surfaced in the same export
// lists as the Report Builder's templates. The transport lives in api/storyfolio-gateway.ts; this
// module is the catalogue and the generation calls.

/**
 * How long the catalogue listing may take before the export menu gives up on it.
 *
 * Far below the shared `REQUEST_TIMEOUT`, which is half an hour: that ceiling is sized for report
 * generation and optimisations, and an export menu inheriting it would hang on a gateway that never
 * answers. Listing is metadata only — no concept data is fetched and nothing is rendered — so it is
 * either quick or it is broken.
 */
const LIST_TIMEOUT_MS = 10_000;

const lists = {
	templates: "templates",
};

const { newQuery, newAction, invalidateEntity, invalidateLists } = repositoryHelperFactory("storyfolio-v2", {
	listKeys: Object.values(lists),
});

const templateApi = () => getStoryfolioGatewayApi(TemplateDiscoveryApiFactory);
const artifactApi = () => getStoryfolioGatewayApi(ArtifactGenerationApiFactory);

/**
 * Whether this session may see Storyfolio V2 templates at all.
 *
 * Two independent conditions: the environment has a gateway deployed, and the customer holds
 * `STORYFOLIO_V2`. Checked here rather than at each call site so that a caller cannot list the
 * catalogue on an environment where `getStoryfolioGatewayApi` would throw.
 */
export function storyfolioV2Enabled(): boolean {
	return storyfolioGatewayAvailable() && hasAccess(getCurrentUser(), { requiredService: ServiceType.StoryfolioV2 });
}

/**
 * One correlation id per gateway call, echoed by the gateway and carried through the Orchestrator,
 * Artifact and Data Manager logs — so "my factsheet failed" comes with the one string that finds
 * the whole flow.
 */
function correlationId(): string {
	return `sphere-web-${crypto.randomUUID()}`;
}

export const storyfolioV2Repository = {
	invalidateEntity,
	invalidateLists,

	/**
	 * The templates this customer may generate, narrowed to the ones that need no written
	 * commentary.
	 *
	 * `commentaryFree` is the gateway's own filter: a template declaring comment regions expects
	 * prose the caller writes, and nothing in the export flow collects any. Empty — rather than an
	 * error — where the environment has no gateway or the customer is not entitled, so the merged
	 * export list is simply the Report Builder's own.
	 */
	templates: newQuery(
		lists.templates,
		async (): Promise<TemplateSummary[]> => {
			if (!storyfolioV2Enabled()) {
				return [];
			}
			const { templates } = await axiosExtract(
				templateApi().listTemplates(correlationId(), undefined, true, { timeout: LIST_TIMEOUT_MS }),
			);
			return templates;
		},
		// No retry: the merged export list waits on this one, so three attempts with backoff would
		// hold the Report Builder's own templates behind a second service's outage. One attempt,
		// bounded by its own timeout, then the list goes out without these entries.
		{ retry: false },
	),

	/**
	 * Generate one document and hand back its bytes.
	 *
	 * `inline` rather than `s3-link` because the caller names the file: the export flow's naming
	 * convention is the platform's, not the gateway's, and a presigned link would arrive under the
	 * archive member's own name.
	 */
	generate: newAction(
		async (request: {
			investmentId: string;
			templateId: string;
			language?: string;
			/** Only a proposal names its stage: the current composition is the gateway's default. */
			stage?: typeof GenerateArtifactRequestStageEnum.Proposal;
		}) => {
			const blob = await axiosExtract(
				artifactApi().generateArtifact(
					{
						investmentId: request.investmentId,
						// TODO(MDOT-25287): no `templateVersion` is sent, so the gateway picks the template's current
						// version. Does it resolve an omitted version once per bulk batch or once per entry? Confirm
						// before deciding whether to send the listed `templateVersion` here and in the bulk entries.
						templateId: request.templateId,
						format: GenerateArtifactRequestFormatEnum.Pptx,
						delivery: GenerateArtifactRequestDeliveryEnum.Inline,
						...(request.language ? { language: request.language } : {}),
						...(request.stage ? { stage: request.stage } : {}),
					},
					correlationId(),
					{ responseType: "blob" },
				),
			);
			return blob as unknown as Blob;
		},
	),

	/**
	 * Generate a batch of documents, returning the archive's presigned URL and one outcome per
	 * requested document.
	 *
	 * `s3-link` rather than `inline`, which is the opposite of the single-document call and for the
	 * same reason: the caller needs to know WHICH documents failed. Both modes put an
	 * `outcomes.json` in the archive, but reading it inline would mean unzipping in the browser,
	 * whereas the `s3-link` envelope carries the outcomes as JSON alongside the archive's URL.
	 *
	 * The URL is handed back rather than fetched: an XHR to the bucket is refused by CORS, so the
	 * archive reaches the user as a browser navigation (`downloadUrl`).
	 */
	generateBulk: newAction(async (documents: BulkDocumentRequest[]) => {
		const response = await axiosExtract(
			artifactApi().generateArtifactsBulk(
				{ documents, delivery: GenerateArtifactsBulkRequestDeliveryEnum.S3Link },
				correlationId(),
			),
		);
		return { archiveUrl: response.archive.url, outcomes: response.outcomes };
	}),
} as const;

export type { BulkDocumentOutcome, BulkDocumentRequest, TemplateSummary };
export { GenerateArtifactRequestStageEnum };
